import { pathFromUrl, storage } from "./storage-client";

/**
 * Avatar upload — the ONE implementation.
 *
 * This file previously existed three times (core, app, admin) with app and admin
 * byte-identical and core subtly different. Three copies of a rule means three
 * places for it to drift, so the other two now re-export this one.
 *
 * `userId` is still accepted so call sites do not change, but it is no longer used
 * to build the object path: the server derives the path from the verified token.
 * That is the whole point — a client-supplied `{userId}/{file}` path is how one
 * user ends up writing into another user's namespace.
 */

const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
const AVATAR_BUCKET = "avatars";

export interface UploadAvatarResult {
  url: string;
  path: string;
}

export interface UploadAvatarError {
  message: string;
  code?: string;
}

/** Client-side pre-check: fail fast before sending bytes over the wire. */
export const validateAvatarFile = (file: File): { valid: boolean; error?: string } => {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return { valid: false, error: "Invalid file type. Please upload a JPG, PNG, GIF, or WebP image." };
  }
  if (file.size > MAX_FILE_SIZE) {
    return { valid: false, error: `File size too large. Maximum size is ${MAX_FILE_SIZE / 1024 / 1024}MB.` };
  }
  return { valid: true };
};

/**
 * Upload an avatar.
 *
 * @param file the image
 * @param _userId kept for signature compatibility; the server decides the path
 */
export const uploadAvatar = async (file: File, _userId?: string): Promise<UploadAvatarResult> => {
  const validation = validateAvatarFile(file);
  if (!validation.valid) throw new Error(validation.error);

  const { data, error } = await storage.from(AVATAR_BUCKET).upload(file, { contentType: file.type });
  if (error) throw new Error(`Failed to upload avatar: ${error.message}`);
  if (!data) throw new Error("Failed to upload avatar");

  return { url: data.url, path: data.path };
};

/** Delete an avatar by its stored path. Unknown paths are ignored by the server. */
export const deleteAvatar = async (path: string): Promise<void> => {
  if (!path) return;
  const { error } = await storage.from(AVATAR_BUCKET).remove([path]);
  if (error) throw new Error(`Failed to delete avatar: ${error.message}`);
};

/**
 * Recover the object key from a stored avatar URL.
 *
 * Handles both the current `?path=` form and the legacy Supabase
 * `/storage/v1/object/public/avatars/{file}` form, because existing
 * `profiles.avatar_url` rows still contain the latter.
 */
export const getAvatarPathFromUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;

  const fromQuery = pathFromUrl(url);
  if (fromQuery) return fromQuery;

  try {
    const pathParts = new URL(url).pathname.split("/");
    const bucketIndex = pathParts.indexOf(AVATAR_BUCKET);
    if (bucketIndex !== -1 && pathParts.length > bucketIndex + 1) {
      return pathParts.slice(bucketIndex + 1).join("/");
    }
  } catch {
    // Not a URL at all.
  }
  return null;
};
